package ru.villawonka.app.presentation.sharedviews.postList.compose.view

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Icon
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import ru.villawonka.app.common.extensions.priceFormat
import ru.villawonka.app.presentation.design.components.AppButton
import ru.villawonka.app.presentation.design.components.AppButtonSize
import ru.villawonka.app.presentation.design.components.AppButtonType
import ru.villawonka.app.presentation.design.components.AppFilledChip
import ru.villawonka.app.presentation.design.components.AppIconButton
import ru.villawonka.app.presentation.design.components.AppIconButtonType
import ru.villawonka.app.presentation.design.components.AppOutlinedChip
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.onSurfacePrimary
import ru.villawonka.app.presentation.design.theme.surfaceTop
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.sharedviews.PhotoCarouselType
import ru.villawonka.app.presentation.sharedviews.PhotoCarouselView
import ru.villawonka.app.presentation.sharedviews.postList.compose.PostListCallbacks
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_chat_24
import villawonka.composeapp.generated.resources.ic_heart_24
import villawonka.composeapp.generated.resources.ic_heart_outline_24
import villawonka.composeapp.generated.resources.ic_map_pin_16
import villawonka.composeapp.generated.resources.ic_nav_arrow_down_24
import villawonka.composeapp.generated.resources.ic_warning_circle_24

@Composable
fun PostItemLayout(
    post: PostUIModel,
    callbacks: PostListCallbacks,
    listState: LazyListState,
    maxWidth: Float,
) {
    val radius = 16.dp
    val nestedScrollEnabled by remember {
        derivedStateOf {
            !listState.isScrollInProgress
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colors.surfaceTop,
                shape = RoundedCornerShape(radius)
            )
            .clickable(
                indication = null,
                interactionSource = null,
                onClick = { callbacks.onPostClick(post) }
            )
            .clip(RoundedCornerShape(radius))
    ) {
        AppSpacer(16.dp)
        PhotoCarouselView(
            photos = post.media,
            type = PhotoCarouselType.COMPACT,
            maxWidth = maxWidth,
            onImageEnlargeClick = { callbacks.onImageEnlargeButtonClick(post, it) },
            badge = { page -> if (page == 0) FloatingBadgesView(post) },
            userScrollEnabled = nestedScrollEnabled
        )
        AppSpacer(16.dp)
        when (post) {
            is PostUIModel.Accommodation -> {
                PostActionsView(post, callbacks)
                AppSpacer(16.dp)
                PostChipsView(post, listState)
                AppSpacer(16.dp)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                ) {
                    post.contactUsername?.let {
                        AppButton(
                            icon = Res.drawable.ic_chat_24,
                            type = AppButtonType.PRIMARY,
                            size = AppButtonSize.MEDIUM,
                            text = "Chat",
                            onClick = { callbacks.onChatButtonClick(post) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                    if (post.contactUsername != null && post.contactPhoneNumber != null) {
                        AppSpacer(8.dp)
                    }
                    post.contactPhoneNumber?.let {
                        AppButton(
                            type = AppButtonType.SECONDARY,
                            size = AppButtonSize.MEDIUM,
                            text = "Call",
                            onClick = { callbacks.onCallButtonClick(post) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
                if (post.similarCount > 0) {
                    AppSpacer(12.dp)
                    AppButton(
                        icon = Res.drawable.ic_nav_arrow_down_24,
                        type = AppButtonType.TERTIARY,
                        size = AppButtonSize.MEDIUM,
                        text = formatVariantsCount(post.similarCount),
                        onClick = { callbacks.onShowSimilarButtonClick(post) },
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp)
                    )
                }
            }
        }
        AppSpacer(16.dp)
    }
}

fun formatVariantsCount(count: Int): String {
    return when (count) {
        1 -> "1 more option"
        else -> "$count more options"
    }
}

@Composable
private fun PostActionsView(
    post: PostUIModel.Accommodation,
    callbacks: PostListCallbacks,
) {
    Row(
        modifier = Modifier.padding(horizontal = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = buildAnnotatedString {
                        withStyle(
                            style = SpanStyle(
                                fontWeight = FontWeight.Bold,
                                fontSize = AppTextStyles.titleMediumBold.fontSize,
                                color = MaterialTheme.colors.onSurfacePrimary
                            )
                        ) {
                            append(post.displayCost.priceFormat())
                        }
                        withStyle(
                            style = SpanStyle(
                                fontSize = AppTextStyles.bodyMediumRegular.fontSize,
                                color = MaterialTheme.colors.onSurfacePrimary
                            )
                        ) {
                            append(" ${post.displayCurrency.code}/${post.rentType.value.lowercase()}")
                        }
                    },
                    modifier = Modifier.align(Alignment.Bottom)
                )
                post.conversationDetails?.let {
                    AppSpacer(8.dp)
                    Icon(
                        painter = painterResource(Res.drawable.ic_warning_circle_24),
                        contentDescription = null,
                        tint = MaterialTheme.colors.onSurfacePrimary,
                        modifier = Modifier
                            .size(16.dp)
                            .clickable(
                                interactionSource = null,
                                indication = null,
                                onClick = { callbacks.onConversionDetailsClick(post) }
                            ),
                    )
                }
            }
            AppSpacer(8.dp)
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    painter = painterResource(Res.drawable.ic_map_pin_16),
                    contentDescription = null,
                    tint = MaterialTheme.colors.onSurfacePrimary
                )
                AppSpacer(4.dp)
                Text(
                    text = post.location,
                    style = AppTextStyles.bodyMediumRegular,
                    color = MaterialTheme.colors.onSurfacePrimary
                )
            }
        }

//        post.contact?.let {
//            AppIconButton(
//                iconRes = Res.drawable.ic_chat_24,
//                enabled = true,
//                onClick = { callbacks.onChatButtonClick(post) }
//            )
//            AppSpacer(12.dp)
//        }

        val isFavorite = post.isLiked

        AppIconButton(
            iconRes = if (isFavorite) {
                Res.drawable.ic_heart_24
            } else {
                Res.drawable.ic_heart_outline_24
            },
            enabled = true,
            selected = isFavorite,
            type =  AppIconButtonType.FAVORITE,
            onClick = { callbacks.onLikeButtonClick(post, !isFavorite) }
        )
    }
}

@Composable
private fun PostChipsView(
    post: PostUIModel,
    lazyListState: LazyListState,
) {
    Row(
        modifier = Modifier
            .horizontalScroll(rememberScrollState())
            .pointerInput(Unit) {
                detectVerticalDragGestures { change, dragAmount ->
                    // При вертикальном скролле передаём его LazyColumn
                    GlobalScope.launch {
                        lazyListState.scrollBy(-dragAmount)
                    }
                }
            }
    ) {
        when (post) {
            is PostUIModel.Accommodation -> AccommodationChipsView(post)
        }
    }
}

@Composable
private fun RowScope.AccommodationChipsView(accommodation: PostUIModel.Accommodation) {
    AppSpacer(16.dp)
    AppOutlinedChip(
        text = accommodation.accommodationType.value,
    )
    accommodation.bedrooms?.let {
        AppSpacer(8.dp)
        AppOutlinedChip(
            text = "$it bedrooms",
        )
    }
    accommodation.additions.take(3).forEach {
        AppSpacer(8.dp)
        AppOutlinedChip(
            text = it.key
        )
    }
    AppSpacer(16.dp)
}